import React from 'react';
import styled from 'styled-components';
import PageBanner from '../components/common/PageBanner';
import ContentSection from '../components/common/ContentSection';
import SectionTitle from '../components/common/SectionTitle';
import { FaShieldAlt, FaQrcode, FaUserCheck, FaFileInvoice, FaChartBar } from 'react-icons/fa';
import { theme } from '../styles/GlobalStyles';

const SolutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 1.5rem;
  margin-top: 3rem;
`;

const SolutionCard = styled.div`
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 2.25rem;
  backdrop-filter: blur(12px);
  box-shadow: ${theme.cardShadow};
  transition: all 0.3s ease;

  &:hover {
    background: ${theme.cardBgHover};
    border-color: rgba(97,93,255,0.35);
    transform: translateY(-6px);
    box-shadow: 0 16px 48px rgba(97,93,255,0.18);
    border-bottom: 3px solid #615DFF;
  }
`;

const IconContainer = styled.div`
  font-size: 2rem;
  margin-bottom: 1.25rem;
  background: ${theme.gradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SolutionTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  color: ${theme.text};
`;

const SolutionDescription = styled.p`
  color: ${theme.textMuted};
  line-height: 1.65;
  margin-bottom: 1.25rem;
  font-size: 0.92rem;
`;

const FeatureList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin-top: 0.75rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.li`
  color: ${theme.textMuted};
  font-size: 0.88rem;
  padding-left: 1.4rem;
  position: relative;

  &::before {
    content: "✓";
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    position: absolute;
    left: 0;
    font-weight: 700;
  }
`;

const WarrantySection = styled.div`
  margin-top: 5rem;
  background: ${theme.cardBg};
  border: 1px solid ${theme.border};
  backdrop-filter: blur(14px);
  padding: 3rem;
  border-radius: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;

  @media (max-width: 992px) { grid-template-columns: 1fr; }
`;

const WarrantyImage = styled.div`
  background-image: url('https://images.unsplash.com/photo-1601972599720-36938d4ecd31?ixlib=rb-4.0.3&auto=format&fit=crop&w=687&q=80');
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  height: 380px;
  border: 1px solid ${theme.border};

  @media (max-width: 992px) { height: 260px; }
`;

const WarrantyContent = styled.div`
  h3 {
    background: ${theme.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-size: 1.7rem;
    margin-bottom: 1.25rem;
  }

  p {
    margin-bottom: 1.25rem;
    line-height: 1.8;
    font-size: 1rem;
    color: ${theme.textMuted};
  }
`;

const solutions = [
  {
    icon: <FaShieldAlt />,
    title: 'Secure Product Authentication',
    desc: 'Protect your products with unbreakable authentication codes that ensure consumers know they have the real deal.',
    features: ['Secure onboarding of every product unit', 'Batch or single-unit registration', 'Automated data validation and security checks'],
  },
  {
    icon: <FaQrcode />,
    title: 'Smart QR Codes',
    desc: 'Our advanced QR technology provides unique identifiers for every product while remaining cost-effective and simple to implement.',
    features: ['Unique identifiers for every product', 'Optional scratch-off protection layer', 'Seamless integration with packaging'],
  },
  {
    icon: <FaUserCheck />,
    title: 'Consumer Verification Portal',
    desc: 'Give your customers confidence with instant verification results that are easy to understand and build trust.',
    features: ['Scan for instant "Genuine" or "Suspected Fake" result', 'Branded verification screens', 'Multi-language support'],
  },
  {
    icon: <FaFileInvoice />,
    title: 'Warranty Seal',
    desc: 'Transform product warranties from paperwork to digital assets that activate automatically and provide value to both customers and brands.',
    features: ['Instant warranty activation upon verification', 'Digital warranty cards stored for consumers', 'Customer lifecycle data for brands'],
  },
  {
    icon: <FaChartBar />,
    title: 'Analytics & Reporting',
    desc: 'Gain valuable insights from verification data to protect your brand and understand your market better.',
    features: ['Track verification scans by time & location', 'Spot counterfeit hotspots early', 'Monitor warranty activations'],
  },
];

const SolutionsPage = () => (
  <>
    <PageBanner
      title="Our Solutions"
      subtitle="Simple and powerful product authentication technology to protect your brand and delight your customers."
    />

    <ContentSection>
      <SectionTitle>Authentication Made Simple</SectionTitle>
      <SolutionGrid>
        {solutions.map((s) => (
          <SolutionCard key={s.title}>
            <IconContainer>{s.icon}</IconContainer>
            <SolutionTitle>{s.title}</SolutionTitle>
            <SolutionDescription>{s.desc}</SolutionDescription>
            <FeatureList>
              {s.features.map((f) => <FeatureItem key={f}>{f}</FeatureItem>)}
            </FeatureList>
          </SolutionCard>
        ))}
      </SolutionGrid>

      <WarrantySection>
        <WarrantyContent>
          <h3>Warranty Seal: Our Latest Innovation</h3>
          <p>
            Warranty Seal is our newest feature, designed to transform the warranty experience
            for both brands and consumers. When a customer verifies product authenticity,
            they can activate their warranty in the same seamless flow.
          </p>
          <p>
            For consumers, no more keeping track of paper warranty cards. For brands, it creates
            a direct connection with customers and provides invaluable data on product activations.
          </p>
          <FeatureList>
            {[
              'Instant warranty activation upon product verification',
              'Digital warranty card stored in consumer\'s digital wallet',
              'Real-time data on purchases and customer lifecycle',
              'Seamless warranty claim process',
              'API integration with existing warranty systems',
            ].map((f) => <FeatureItem key={f}>{f}</FeatureItem>)}
          </FeatureList>
        </WarrantyContent>
        <WarrantyImage />
      </WarrantySection>
    </ContentSection>
  </>
);

export default SolutionsPage;
